import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductsAdmin = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    concentration: '',
    price: '',
    sku: '',
    category: '',
    description: '',
    purity: '',
    coa_image: '',
    product_image: '',
    batch_number: '',
    stock_count: '50',
    in_stock: true,
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const fieldValue = type === 'checkbox' ? checked : (name === 'price' || name === 'stock_count' ? parseFloat(value) : value);
    setFormData({
      ...formData,
      [name]: fieldValue,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, formData);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/products`, formData);
        toast.success('Product created successfully');
      }
      fetchProducts();
      closeModal();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      concentration: product.concentration,
      price: product.price,
      sku: product.sku,
      category: product.category,
      description: product.description,
      purity: product.purity,
      coa_image: product.coa_image || '',
      product_image: product.product_image || '',
      batch_number: product.batch_number || '',
      stock_count: product.stock_count || 0,
      in_stock: product.in_stock !== false,
    });
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      concentration: '',
      price: '',
      sku: '',
      category: '',
      description: '',
      purity: '',
      coa_image: '',
      product_image: '',
      batch_number: '',
      stock_count: '50',
      in_stock: true,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  if (loading) {
    return <div className="text-center py-12" data-testid="loading-indicator">Loading products...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-heading font-semibold text-primary-main">Manage Products</h2>
        <button
          onClick={openCreateModal}
          className="btn-primary flex items-center gap-2"
          data-testid="create-product-button"
        >
          <FiPlus /> Add Product
        </button>
      </div>

      {/* Products Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background-subtle">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">SKU</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Name</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Concentration</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Price</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Stock</th>
                <th className="px-6 py-4 text-left text-sm font-heading font-semibold text-primary-main">Status</th>
                <th className="px-6 py-4 text-right text-sm font-heading font-semibold text-primary-main">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id} className="border-t border-slate-100" data-testid={`product-row-${product.sku}`}>
                  <td className="px-6 py-4 text-sm font-mono text-text-secondary">{product.sku}</td>
                  <td className="px-6 py-4 text-sm text-text-primary font-medium">{product.name}</td>
                  <td className="px-6 py-4 text-sm text-text-secondary">{product.concentration}</td>
                  <td className="px-6 py-4 text-sm font-mono text-text-primary">${product.price} AUD</td>
                  <td className="px-6 py-4 text-sm font-mono text-text-primary">{product.stock_count || 0}</td>
                  <td className="px-6 py-4">
                    {product.in_stock ? (
                      <span className="inline-block px-2 py-1 bg-success/10 text-success text-xs font-medium rounded">
                        In Stock
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-1 bg-error/10 text-error text-xs font-medium rounded">
                        Out of Stock
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleEdit(product)}
                      className="text-accent-main hover:text-accent-main/80 mr-3"
                      data-testid={`edit-product-${product.sku}`}
                    >
                      <FiEdit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="text-error hover:text-red-700"
                      data-testid={`delete-product-${product.sku}`}
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="product-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <h3 className="text-2xl font-heading font-bold text-primary-main mb-6">
                {editingProduct ? 'Edit Product' : 'Create Product'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                      data-testid="product-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">SKU *</label>
                    <input
                      type="text"
                      name="sku"
                      value={formData.sku}
                      onChange={handleChange}
                      required
                      className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                      data-testid="product-sku-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Concentration *</label>
                    <input
                      type="text"
                      name="concentration"
                      value={formData.concentration}
                      onChange={handleChange}
                      required
                      className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                      data-testid="product-concentration-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Price (AUD) *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      required
                      className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                      data-testid="product-price-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Category *</label>
                    <input
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                      data-testid="product-category-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Purity *</label>
                    <input
                      type="text"
                      name="purity"
                      value={formData.purity}
                      onChange={handleChange}
                      required
                      placeholder="e.g., ≥99.0%"
                      className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                      data-testid="product-purity-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                    data-testid="product-description-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Product Image URL</label>
                  <input
                    type="url"
                    name="product_image"
                    value={formData.product_image}
                    onChange={handleChange}
                    placeholder="https://example.com/product.jpg"
                    className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                    data-testid="product-image-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">COA Image URL</label>
                  <input
                    type="url"
                    name="coa_image"
                    value={formData.coa_image}
                    onChange={handleChange}
                    placeholder="https://example.com/coa.png"
                    className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                    data-testid="product-coa-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">Batch Number</label>
                  <input
                    type="text"
                    name="batch_number"
                    value={formData.batch_number}
                    onChange={handleChange}
                    placeholder="e.g., 2026.1.28"
                    className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                    data-testid="product-batch-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Stock Count *</label>
                    <input
                      type="number"
                      name="stock_count"
                      value={formData.stock_count}
                      onChange={handleChange}
                      required
                      min="0"
                      className="w-full border border-slate-200 rounded-md px-4 py-2 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                      data-testid="product-stock-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">Availability</label>
                    <label className="flex items-center gap-2 mt-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="in_stock"
                        checked={formData.in_stock}
                        onChange={handleChange}
                        className="w-4 h-4 rounded border-slate-300 focus:ring-primary-main"
                        data-testid="product-instock-input"
                      />
                      <span className="text-sm text-text-secondary">In Stock</span>
                    </label>
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary flex-1"
                    data-testid="cancel-product-button"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary flex-1" data-testid="save-product-button">
                    {editingProduct ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsAdmin;
